import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, MagicWand, CircleNotch, Buildings, MapPin, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import GoodsReceiptLines from "@/components/warehouse/GoodsReceiptLines";

/** Blank line row. */
let _lineSeq = 0;
const _makeKey = () => `ln-${Date.now().toString(36)}-${(_lineSeq += 1)}`;

const emptyLine = () => ({
  _key: _makeKey(),
  description: "",
  quantity: 1,
  unit_price: 0,
  unit: "nos",
  hsn: "",
  sku: "",
  destination: "warehouse",
  warehouse_id: "",
  project_id: "",
});

/**
 * Goods Receipt — the single most user-friendly stock-in flow.
 *
 *  1. Upload a vendor bill (PDF/JPG/PNG). Backend parses every line via AI.
 *  2. All lines land in an editable table. Each row has a "Destination" picker:
 *       - Warehouse (choose which)   — increments stock + logs movement
 *       - Direct to Site (project)   — creates a project purchase (project P&L)
 *  3. One-click bulk actions to route all rows to a single destination.
 *  4. Save → creates a Goods Receipt (`GRN-000123`) with the attached PDF and
 *       posts each line to the correct downstream table in one transaction.
 */
export default function GoodsReceipt() {
  const nav = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parseErr, setParseErr] = useState("");
  const [header, setHeader] = useState({ vendor: "", invoice_number: "", invoice_date: "", notes: "" });
  const [lines, setLines] = useState([emptyLine()]);

  // Bulk-set default destination for freshly-parsed lines
  const [defaultDestination, setDefaultDestination] = useState("warehouse");
  const [defaultWarehouse, setDefaultWarehouse] = useState("");
  const [defaultProject, setDefaultProject] = useState("");

  const load = useCallback(async () => {
    try {
      const [w, p] = await Promise.all([api.get("/warehouses"), api.get("/projects")]);
      setWarehouses(w.data);
      setProjects(p.data);
      if (w.data.length > 0) setDefaultWarehouse(w.data[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyDefaultToLines = useCallback((rows) => rows.map((r) => ({
    ...r,
    destination: defaultDestination,
    warehouse_id: defaultDestination === "warehouse" ? defaultWarehouse : "",
    project_id: defaultDestination === "site" ? defaultProject : "",
  })), [defaultDestination, defaultWarehouse, defaultProject]);

  const parsePdf = useCallback(async (chosen) => {
    if (!chosen) return;
    setParsing(true); setParseErr("");
    try {
      const fd = new FormData();
      fd.append("file", chosen);
      const { data } = await api.post("/goods-receipts/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setHeader({
        vendor: data.vendor || "",
        invoice_number: data.invoice_number || "",
        invoice_date: data.invoice_date || "",
        notes: "",
      });
      const parsedLines = (data.lines || []).map((ln) => ({
        ...emptyLine(),
        description: ln.description || "",
        quantity: ln.quantity || 1,
        unit_price: ln.unit_price || 0,
        unit: ln.unit || "nos",
        hsn: ln.hsn || "",
        sku: ln.sku || "",
      }));
      setLines(applyDefaultToLines(parsedLines.length > 0 ? parsedLines : [emptyLine()]));
      toast.success(`Parsed ${parsedLines.length} line items — review destinations & save`);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setParseErr(msg);
      toast.warning(`Auto-parse failed: ${msg}. Fill lines manually.`);
    } finally {
      setParsing(false);
    }
  }, [applyDefaultToLines]);

  const onFilePicked = (e) => {
    const chosen = e.target.files?.[0] || null;
    setFile(chosen);
    setParseErr("");
    if (chosen) parsePdf(chosen);
  };

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0),
    [lines]
  );

  const warehouseTotal = useMemo(
    () => lines.filter((l) => l.destination === "warehouse").reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0),
    [lines]
  );
  const siteTotal = useMemo(
    () => lines.filter((l) => l.destination === "site").reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0),
    [lines]
  );

  const updateLine = (idx, patch) => setLines((rows) => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeLine = (idx) => setLines((rows) => rows.filter((_, i) => i !== idx));
  const addLine = () => setLines((rows) => [...rows, { ...emptyLine(), warehouse_id: defaultWarehouse, destination: defaultDestination }]);

  const routeAllTo = (destination, target) => {
    setLines((rows) => rows.map((r) => ({
      ...r,
      destination,
      warehouse_id: destination === "warehouse" ? target : "",
      project_id: destination === "site" ? target : "",
    })));
    toast.success(destination === "warehouse" ? "All lines routed to warehouse" : "All lines routed to site");
  };

  const save = async () => {
    if (!header.vendor) { toast.error("Vendor name is required"); return; }
    if (lines.length === 0) { toast.error("Add at least one line"); return; }
    for (let i = 0; i < lines.length; i += 1) {
      const l = lines[i];
      if (!l.description) { toast.error(`Line ${i + 1}: description required`); return; }
      if (l.destination === "warehouse" && !l.warehouse_id) { toast.error(`Line ${i + 1}: pick a warehouse`); return; }
      if (l.destination === "site" && !l.project_id) { toast.error(`Line ${i + 1}: pick a site/project`); return; }
    }
    setSaving(true);
    try {
      const { data: grn } = await api.post("/goods-receipts", {
        vendor: header.vendor,
        invoice_number: header.invoice_number,
        invoice_date: header.invoice_date,
        notes: header.notes,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          unit: l.unit || "nos",
          hsn: l.hsn || "",
          sku: l.sku || "",
          destination: l.destination,
          warehouse_id: l.destination === "warehouse" ? l.warehouse_id : null,
          project_id: l.destination === "site" ? l.project_id : null,
        })),
      });

      // Attach the PDF to the receipt
      if (file && grn?.id) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("parent_type", "goods_receipt");
        fd.append("parent_id", grn.id);
        fd.append("category", "general");
        fd.append("title", `Vendor Invoice · ${header.vendor}`);
        try {
          await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (attErr) {
          devLog.warn("Attachment upload failed", attErr);
        }
      }

      toast.success(`Goods Receipt ${grn.receipt_number} saved · ${grn.warehouse_line_count} to stock · ${grn.site_line_count} to site`);
      nav("/goods-receipts");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="goods-receipt-page">
      <PageHeader
        title="Receive Goods"
        subtitle="Upload the vendor invoice. AI extracts every line — you route each to warehouse or site."
        module="Warehouse · Inbound"
      />

      <div className="p-8 max-w-6xl">
        {/* Step 1 · Upload */}
        <section className="bg-white border border-slate-200 rounded-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-widest font-semibold bg-[#EA580C] text-white px-1.5 py-0.5 rounded-sm">Step 1</span>
            <span className="text-[15px] font-semibold font-display">Vendor Invoice</span>
            <span className="inline-flex items-center gap-1 text-[9px] tracking-widest font-semibold uppercase bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-sm ml-2">
              <MagicWand size={10} /> AI multi-line auto-fill
            </span>
          </div>
          <label
            htmlFor="grn-file-input"
            className="flex items-center gap-3 border border-dashed border-slate-300 hover:border-[#EA580C] rounded-sm p-4 cursor-pointer bg-slate-50"
          >
            {parsing ? <CircleNotch size={24} className="text-[#EA580C] animate-spin" /> : <FilePdf size={24} className="text-[#EA580C]" />}
            <div className="flex-1 min-w-0">
              {file ? (
                <>
                  <div className="text-sm font-medium truncate">{file.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {(file.size / 1024).toFixed(1)} KB
                    {parsing && <span className="text-[#EA580C] ml-2">· Extracting line items…</span>}
                    {!parsing && !parseErr && lines.length > 1 && (
                      <span className="text-emerald-700 ml-2">· {lines.length} lines parsed ✓</span>
                    )}
                    {!parsing && parseErr && <span className="text-red-600 ml-2">· Manual entry needed</span>}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-medium text-slate-700">Click to choose vendor invoice</div>
                  <div className="text-[11px] text-slate-500">PDF · JPG · PNG · max 20 MB</div>
                </>
              )}
            </div>
            <input
              id="grn-file-input"
              data-testid="grn-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={onFilePicked}
              className="hidden"
            />
          </label>
        </section>

        {/* Step 2 · Header + Bulk destination */}
        <section className="bg-white border border-slate-200 rounded-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10px] uppercase tracking-widest font-semibold bg-slate-800 text-white px-1.5 py-0.5 rounded-sm">Step 2</span>
            <span className="text-[15px] font-semibold font-display">Receipt Details</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="label-caps mb-1 block">Vendor</Label>
              <Input value={header.vendor} onChange={(e) => setHeader({ ...header, vendor: e.target.value })} data-testid="grn-vendor" />
            </div>
            <div>
              <Label className="label-caps mb-1 block">Invoice #</Label>
              <Input value={header.invoice_number} onChange={(e) => setHeader({ ...header, invoice_number: e.target.value })} data-testid="grn-invoice-number" />
            </div>
            <div>
              <Label className="label-caps mb-1 block">Invoice date</Label>
              <Input type="date" value={header.invoice_date} onChange={(e) => setHeader({ ...header, invoice_date: e.target.value })} data-testid="grn-invoice-date" />
            </div>
            <div>
              <Label className="label-caps mb-1 block">Notes</Label>
              <Input value={header.notes} onChange={(e) => setHeader({ ...header, notes: e.target.value })} placeholder="Optional" />
            </div>
          </div>

          {/* Bulk destination controls */}
          <div className="mt-5 p-3 bg-slate-50 border border-slate-200 rounded-sm">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
              Bulk route — send ALL lines to one destination
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-[10px] text-slate-500 mb-1 block">Default warehouse</Label>
                <Select value={defaultWarehouse || "__none__"} onValueChange={(v) => setDefaultWarehouse(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="bulk-warehouse-select"><SelectValue placeholder="Choose warehouse" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} ({w.code || w.city || "—"})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={() => routeAllTo("warehouse", defaultWarehouse)}
                disabled={!defaultWarehouse}
                data-testid="route-all-warehouse"
                className="rounded-sm border-slate-300"
              >
                <Buildings size={14} className="mr-2" /> Send all to warehouse
              </Button>

              <div className="flex-1 min-w-[180px]">
                <Label className="text-[10px] text-slate-500 mb-1 block">Direct-to-site project</Label>
                <Select value={defaultProject || "__none__"} onValueChange={(v) => setDefaultProject(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="bulk-project-select"><SelectValue placeholder="Choose project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={() => routeAllTo("site", defaultProject)}
                disabled={!defaultProject}
                data-testid="route-all-site"
                className="rounded-sm border-slate-300"
              >
                <MapPin size={14} className="mr-2" /> Send all to site
              </Button>
            </div>
          </div>
        </section>

        {/* Step 3 · Line items — extracted component (iter48) */}
        <GoodsReceiptLines
          lines={lines}
          updateLine={updateLine}
          addLine={addLine}
          removeLine={removeLine}
          warehouses={warehouses}
          projects={projects}
          defaultWarehouse={defaultWarehouse}
        />

        {/* Totals summary — kept in parent so we can reuse warehouseTotal/siteTotal/total */}
        <div className="mb-6 -mt-4 px-5 py-3 border border-t-0 border-slate-200 bg-slate-50 flex justify-end gap-6 font-mono-num text-sm rounded-b-sm">
          <div><span className="label-caps mr-2">To warehouse</span> {fmtMoney(warehouseTotal)}</div>
          <div><span className="label-caps mr-2">To site</span> {fmtMoney(siteTotal)}</div>
          <div className="border-l border-slate-300 pl-6 font-bold text-[#EA580C]"><span className="label-caps mr-2 text-slate-500">Grand Total</span> {fmtMoney(total)}</div>
        </div>


        {/* Save */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => nav("/goods-receipts")} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving || parsing || lines.length === 0} data-testid="save-goods-receipt" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white px-8">
            {saving ? "Saving…" : "Save Goods Receipt"}
          </Button>
        </div>
      </div>
    </div>
  );
}
